import api from './api';
import { Invoice, InvoiceStatus, AuditLog } from '../types';

export interface InvoiceFilters {
  status?: InvoiceStatus | string;
  month?: string;
  memberName?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedInvoiceResponse {
  invoices: Invoice[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateInvoiceDto {
  invoiceMonth: string;
  memberId?: string;
  notes?: string;
  vendorName?: string;
}

export interface UpdateInvoiceDto {
  status?: InvoiceStatus;
  reviewerComments?: string;
  overrideTotal?: number;
  notes?: string;
}

export interface BulkUpdateStatusDto {
  ids: string[];
  status: InvoiceStatus;
  reviewerComments?: string;
}

export interface InvoiceStatistics {
  total: number;
  pending: number;
  underReview: number;
  approved: number;
  rejected: number;
  paid: number;
}

/**
 * Invoice Service
 * Centralizes all invoice-related API calls
 */
class InvoiceService {
  private baseUrl = '/invoices';

  /**
   * Get all invoices with optional filters and pagination
   * Maps Spring Boot Page<T> response to PaginatedInvoiceResponse
   */
  async getAll(filters?: InvoiceFilters): Promise<PaginatedInvoiceResponse> {
    const params = new URLSearchParams();
    
    if (filters?.status && filters.status !== '__all__') {
      params.append('status', filters.status);
    }
    if (filters?.month && filters.month !== '__all__') {
      params.append('month', filters.month);
    }
    if (filters?.memberName) {
      params.append('memberName', filters.memberName);
    }
    if (filters?.page) {
      // Spring Boot uses 0-indexed pages
      params.append('page', (filters.page - 1).toString());
    }
    if (filters?.limit) {
      params.append('size', filters.limit.toString());
    }
    // Sort by month descending so paginated results stay correctly ordered server-side
    params.append('sort', 'invoiceMonth,desc');

    const url = params.toString() ? `${this.baseUrl}?${params.toString()}` : this.baseUrl;
    const response = await api.get(url);
    const data = response.data;

    // Handle Spring Boot Page<T> response format
    if (data && 'content' in data) {
      return {
        invoices: data.content,
        total: data.totalElements,
        page: data.number + 1, // Convert 0-indexed to 1-indexed
        totalPages: data.totalPages,
      };
    }

    // Fallback for legacy flat response format
    return data;
  }

  /**
   * Get a single invoice by ID
   */
  async getById(id: string): Promise<Invoice> {
    const response = await api.get<Invoice>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new invoice
   * memberId is optional — the backend auto-resolves from the authenticated user if omitted.
   */
  async create(data: CreateInvoiceDto, file: File): Promise<Invoice> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('invoiceMonth', data.invoiceMonth);
    if (data.memberId) {
      formData.append('memberId', data.memberId);
    }
    if (data.notes) {
      formData.append('notes', data.notes);
    }
    if (data.vendorName) {
      formData.append('vendorName', data.vendorName);
    }

    const response = await api.post<Invoice>(this.baseUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Update an invoice
   */
  async update(id: string, data: UpdateInvoiceDto): Promise<Invoice> {
    const response = await api.patch<Invoice>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete an invoice
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Bulk update invoice status
   */
  async bulkUpdateStatus(data: BulkUpdateStatusDto): Promise<void> {
    await api.patch(`${this.baseUrl}/bulk-update-status`, data);
  }

  /**
   * Get audit logs for an invoice
   * Handles Spring Boot Page<T> response format
   */
  async getAuditLogs(id: string): Promise<AuditLog[]> {
    const response = await api.get(`${this.baseUrl}/${id}/audit-logs`);
    const data = response.data;

    // Handle Spring Boot Page<T> response format
    if (data && 'content' in data) {
      return data.content;
    }

    // Fallback for legacy flat array format
    return data;
  }

  /**
   * Get invoice statistics
   */
  async getStatistics(month?: string): Promise<InvoiceStatistics> {
    const params = month ? `?month=${month}` : '';
    const response = await api.get<InvoiceStatistics>(`${this.baseUrl}/statistics${params}`);
    return response.data;
  }

  /**
   * Download invoice file
   */
  getDownloadUrl(invoice: Invoice): string {
    return invoice.fileUrl;
  }
}

// Export singleton instance
export const invoiceService = new InvoiceService();
